import { useReducer, useMemo, useEffect, useState } from "react";
import { ACTION_CARD_TYPE, FormReturn } from "../types";
import {
  getAmountWarning,
  getCvvErrMsg,
  getExpErrMsg,
  getHasError,
  getNumErrMsg,
  getReducer,
  initialState
} from "./methods";

const useForm = (
  minLimit: number,
  maxLimit: number,
  visitedFields: Set<string>,
  selectedField: string,
  removeVisitedField: (field: string) => void,
  content: object,
  isBraintreeCCEnabled: boolean = false
): FormReturn => {
  const [formState, formDispatch] = useReducer(getReducer, {
    ...initialState,
    cardType: isBraintreeCCEnabled ? "BTCC" : initialState.cardType
  });
  const [showAmountWarning, setAmountWarning] = useState(false);

  const { amount, cardCVV, cardExp, cardNum, cardType } = formState;

  useEffect(() => {
    const isBrtCode = cardType === "BTCC" || cardType === "BTDB";
    const isLegacyCode = cardType === "CC" || cardType === "DEB";
    if (isBraintreeCCEnabled && !isBrtCode) {
      // Preserve the user's credit/debit choice across the namespace switch.
      const value = cardType === "DEB" ? "BTDB" : "BTCC";
      formDispatch({ type: ACTION_CARD_TYPE, payload: { value } });
    } else if (!isBraintreeCCEnabled && !isLegacyCode) {
      const value = cardType === "BTDB" ? "DEB" : "CC";
      formDispatch({ type: ACTION_CARD_TYPE, payload: { value } });
    }
  }, [isBraintreeCCEnabled]);

  useEffect(
    getAmountWarning(
      amount,
      maxLimit,
      minLimit,
      setAmountWarning,
      showAmountWarning,
      selectedField
    ),
    [amount, maxLimit, minLimit, selectedField]
  );

  const numErrMsg = useMemo<string>(
    getNumErrMsg(
      visitedFields,
      selectedField,
      content,
      cardNum,
      removeVisitedField
    ),
    [cardNum, selectedField]
  );

  const cvvErrMsg = useMemo<string>(
    getCvvErrMsg(
      visitedFields,
      selectedField,
      content,
      cardCVV,
      removeVisitedField
    ),
    [cardCVV, selectedField]
  );

  const expErrMsg = useMemo<string>(
    getExpErrMsg(
      visitedFields,
      selectedField,
      content,
      cardExp,
      removeVisitedField
    ),
    [cardExp, selectedField]
  );

  const hasError = useMemo(
    getHasError(
      cvvErrMsg,
      expErrMsg,
      numErrMsg,
      cardCVV,
      cardExp,
      cardNum,
      amount,
      showAmountWarning
    ),
    [
      showAmountWarning,
      cardNum,
      cardExp,
      cardCVV,
      cvvErrMsg,
      expErrMsg,
      numErrMsg,
      amount
    ]
  );

  return {
    formState,
    formDispatch,
    showAmountWarning,
    expErrMsg,
    numErrMsg,
    cvvErrMsg,
    hasError
  };
};

export default useForm;
